import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../../shared/contexts/StoreContext';
import { useCart } from '../../shared/contexts/CartContext';
import { useToast } from '../../shared/contexts/ToastContext';
import Api from '../../shared/services/api';
import { IProduct } from '../../shared/types/Store';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  max-width: 450px;
  margin: 0 auto;
  padding: 40px 20px;
  text-align: center;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f9fafb;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: ${props => props.theme?.primary || '#f58220'};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin-bottom: 24px;
`;

const Icon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 900;
  color: #111;
  margin: 0 0 12px;
  text-transform: uppercase;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

// Converte array de nomes ["A","A","B"] → { A: 2, B: 1 } para o formato additionalQtys do CartModal
const toQtysMap = (arr: string[]): Record<string, number> =>
  arr.reduce((acc: Record<string, number>, name: string) => {
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});

const mapCartItem = (item: any, products: IProduct[]): { product: IProduct; quantity: number; options?: any } | null => {
  const product = products.find(
    (p: IProduct) => String(p.id) === String(item.id || item.productId)
  );
  if (!product) return null;

  // Suporta qty (salvo pelo AI agent) e quantity (salvo pelo client frontend)
  const quantity = Number(item.qty || item.quantity || 1);

  // Suporta options aninhado (client) e arrays flat (AI agent)
  const hasFlat =
    item.flavors?.length || item.additionals?.length ||
    item.sauces?.length || item.sideDishes?.length ||
    item.salads?.length || item.desserts?.length ||
    item.removedIngredients?.length || item.observations;

  const options = item.options
    ? item.options
    : hasFlat
    ? {
        flavors: item.flavors || [],
        additionals: item.additionals || [],
        // additionalQtys: formato que o CartModal usa para mostrar os selecionados
        additionalQtys: item.additionals?.length ? toQtysMap(item.additionals) : undefined,
        sauces: item.sauces || [],
        sideDishes: item.sideDishes?.length ? item.sideDishes : item.sideDish ? [item.sideDish] : [],
        salads: item.salads || [],
        desserts: item.desserts || [],
        removedIngredients: item.removedIngredients || [],
        observations: item.observations || '',
      }
    : undefined;

  return { product, quantity, options };
};

export const CartRecoveryPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { store, products } = useStore();
  const { clearCart, addItem } = useCart();
  const { showToast } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (!store || !products || products.length === 0 || !token || hasAttempted.current) return;
    hasAttempted.current = true;

    const recover = async () => {
      try {
        const { data } = await Api.get(`/abandoned-carts/recover/${token}`);
        const cart = data?.cart;

        if (!cart?.items?.length) {
          setStatus('error');
          showToast('Carrinho não encontrado ou já finalizado', 'error');
          setTimeout(() => navigate('/'), 2500);
          return;
        }

        // Persistir token para updates futuros de tracking
        if (cart.recoveryToken) {
          localStorage.setItem(`cartRecoveryToken_${store.id}`, cart.recoveryToken);
        }

        const mapped = cart.items
          .map((item: any) => mapCartItem(item, products))
          .filter(Boolean) as { product: IProduct; quantity: number; options?: any }[];

        if (mapped.length === 0) {
          setStatus('error');
          showToast('Os produtos deste carrinho não estão mais disponíveis', 'error');
          setTimeout(() => navigate('/'), 2500);
          return;
        }

        clearCart();
        mapped.forEach(({ product, quantity, options }) => addItem(product, quantity, options));

        // Pré-preencher dados do cliente coletados pelo AI agent
        if (cart.customerName) sessionStorage.setItem('comanda_name', cart.customerName);
        if (cart.phone) {
          // Remove código de país brasileiro (55 + 11 dígitos = 13) para compatibilidade com validação do formulário
          const digits = cart.phone.replace(/\D/g, '');
          const phone11 = digits.length === 13 && digits.startsWith('55') ? digits.slice(2) : digits;
          if (phone11) sessionStorage.setItem('comanda_phone', phone11);
        }
        if (cart.address) sessionStorage.setItem('ai_customer_address', cart.address);
        if (cart.referencePoint) sessionStorage.setItem('ai_customer_reference', cart.referencePoint);
        if (cart.deliveryMode) sessionStorage.setItem('ai_customer_delivery_mode', cart.deliveryMode);

        // Marcar como sessão recuperada para o TrackingService
        sessionStorage.setItem('isRecoveredSession', 'true');

        setStatus('success');
        showToast(
          `Carrinho recuperado! ${mapped.length} ${mapped.length === 1 ? 'item restaurado' : 'itens restaurados'} 🛒`,
          'success'
        );
        navigate('/');
      } catch (_) {
        setStatus('error');
        showToast('Não foi possível recuperar o carrinho', 'error');
        setTimeout(() => navigate('/'), 2500);
      }
    };

    recover();
  }, [store, products, token, clearCart, addItem, showToast, navigate]);

  return (
    <Container>
      {status === 'loading' && (
        <>
          <Spinner />
          <Title>Recuperando Carrinho</Title>
          <Subtitle>Aguarde um momento...</Subtitle>
        </>
      )}
      {status === 'success' && (
        <>
          <Icon>🛒</Icon>
          <Title>Carrinho Recuperado!</Title>
          <Subtitle>Redirecionando para o cardápio...</Subtitle>
        </>
      )}
      {status === 'error' && (
        <>
          <Icon>😕</Icon>
          <Title>Carrinho não encontrado</Title>
          <Subtitle>Redirecionando para o cardápio...</Subtitle>
        </>
      )}
    </Container>
  );
};
